import { create } from 'zustand';
import { boardService } from '../lib/api/boards';
import { useBoardStore, BoardJournal } from './boardStore';

export interface Journal {
    id: string;
    userId: string;
    name: string;
    type: string;
    isEnabled: boolean;
    order: number;
    createdAt: string;
    updatedAt: string;
}

export interface SharedJournal {
    id: string;
    name: string;
    type: string;
    isEnabled: boolean;
    owner: {
        id: string;
        name: string;
    };
    permission: 'read' | 'readwrite';
    sharedAt: string;
}

interface JournalState {
    journals: Journal[];
    sharedJournals: SharedJournal[];
    isLoading: boolean;
    isLoadingShared: boolean;
    error: string | null;
    loadJournals: (force?: boolean) => Promise<void>;
    loadSharedJournals: (force?: boolean) => Promise<void>;
    addJournal: (name: string) => Promise<Journal>;
    deleteJournal: (id: string) => Promise<void>;
    updateJournal: (id: string, data: Partial<Journal>) => Promise<void>;
    removeSharedAccess: (id: string) => Promise<void>;
    reset: () => void;
}

export const useJournalStore = create<JournalState>((set, get) => ({
    journals: [],
    sharedJournals: [],
    isLoading: false,
    isLoadingShared: false,
    error: null,

    loadJournals: async (force = false) => {
        if (!force && get().journals.length > 0) return;

        set({ isLoading: true, error: null });
        try {
            const response = await boardService.getGlobalJournals();
            if (response.success) {
                set({ journals: response.data, isLoading: false });
            } else {
                set({ error: response.error || 'Failed to load journals', isLoading: false });
            }
        } catch (error: any) {
            set({ error: 'Unable to load journals. Please try again.', isLoading: false });
        }
    },

    loadSharedJournals: async (force = false) => {
        if (!force && get().sharedJournals.length > 0) return;

        set({ isLoadingShared: true });
        try {
            const data = await boardService.getSharedJournals();
            set({ sharedJournals: data, isLoadingShared: false });
        } catch (error: any) {
            console.error('Failed to load shared journals', error);
            set({ isLoadingShared: false });
        }
    },

    addJournal: async (name: string) => {
        set({ isLoading: true });
        try {
            const data = await boardService.createGlobalJournal({ name });
            set(state => ({
                journals: [data, ...state.journals],
                isLoading: false
            }));
            return data;
        } catch (error: any) {
            set({ isLoading: false });
            throw error;
        }
    },

    deleteJournal: async (id: string) => {
        try {
            await boardService.deleteGlobalJournal(id);
            set(state => ({
                journals: state.journals.filter(j => j.id !== id)
            }));
            // boardStore.setBoardData preserves journals present in the local
            // store but missing from the incoming payload (optimistic-add
            // guard). Without mirroring the delete here, the journal lingers
            // on boards until a full page refresh.
            useBoardStore.getState().deleteJournal(id);
        } catch (error) {
            console.error('Failed to delete journal', error);
        }
    },

    updateJournal: async (id: string, data: Partial<Journal>) => {
        try {
            const updatedData = await boardService.updateGlobalJournal(id, data);
            set(state => ({
                journals: state.journals.map(j => j.id === id ? { ...j, ...updatedData } : j)
            }));
            useBoardStore.getState().updateJournal(id, updatedData as Partial<BoardJournal>);
        } catch (error) {
            console.error('Failed to update journal', error);
        }
    },

    removeSharedAccess: async (id: string) => {
        try {
            await boardService.removeSharedAccess(id);
            set(state => ({
                sharedJournals: state.sharedJournals.filter(j => j.id !== id)
            }));
            useBoardStore.getState().deleteJournal(id);
        } catch (error) {
            console.error('Failed to remove shared access', error);
        }
    },

    // Reset store (called on logout to clear stale cache)
    reset: () => {
        set({
            journals: [],
            sharedJournals: [],
            isLoading: false,
            isLoadingShared: false,
            error: null
        });
    }
}));

